import {
  Attributes,
  Context,
  context,
  Span,
  SpanKind,
  SpanStatusCode,
  TextMapPropagator,
  TextMapSetter,
  trace,
  Tracer,
} from "@opentelemetry/api";
import { otperformance, W3CTraceContextPropagator } from "@opentelemetry/core";

// B3 propagator is optional - only imported if @opentelemetry/propagator-b3 is installed
let B3Propagator: (new (config?: { injectEncoding?: number }) => TextMapPropagator) | undefined;
let B3InjectEncoding: { MULTI_HEADER: number } | undefined;
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const b3Module = require("@opentelemetry/propagator-b3");
  B3Propagator = b3Module.B3Propagator;
  B3InjectEncoding = b3Module.B3InjectEncoding;
} catch {
  // B3 propagator not installed - will log warning if user tries to use b3 format
}

import CONV from "../semantic-conventions";
import { BackendCorrelationConfig, NetworkInstrumentationOptions, RootSpan } from "../types";
import { error, log, warn } from "../utils/logger";
import { addSpanNetworkEvents } from "./utils";

type NetworkLogLevel = "info" | "warn" | "error";

type ResolvedBackendCorrelationConfig = {
  enabled: boolean;
  propagationFormats: ("w3c" | "b3")[];
  corsAllowedOrigins: string[];
  customHeaders: Record<string, string>;
  injectToAllRequests: boolean;
  normalizedAllowedOrigins: string[];
  forceOverwrite: boolean;
};

const DEFAULT_BACKEND_CORRELATION_CONFIG: ResolvedBackendCorrelationConfig = {
  enabled: true,
  propagationFormats: ["w3c"],
  corsAllowedOrigins: [],
  customHeaders: {},
  injectToAllRequests: false,
  normalizedAllowedOrigins: [],
  forceOverwrite: false,
};

/**
 * List of hostname patterns for telemetry/intake endpoints that should NEVER
 * have trace context headers injected, regardless of configuration.
 * These are internal SDK endpoints used by various RUM/observability tools.
 */
const TELEMETRY_ENDPOINT_PATTERNS: RegExp[] = [
  // DataDog RUM intake endpoints (note: hostname uses hyphens, e.g., browser-intake-datadoghq.com)
  /browser-intake.*datadoghq\.(com|eu)/i,
  /.*-http-intake.*datadoghq\.(com|eu)/i,
  // Last9 OTLP endpoints
  /otlp-ext-.*\.last9\.io/i,
  /otlp-.*\.last9\.io/i,
  // New Relic
  /bam\.nr-data\.net/i,
  // Sentry
  /.*\.ingest\.sentry\.io/i,
  // Dynatrace
  /.*\.dynatrace\.(com|eu)/i,
  // Elastic APM
  /.*\.apm\..*\.cloud\.es\.io/i,
  // Splunk RUM
  /rum-ingest\..*\.signalfx\.com/i,
  // LogRocket
  /r\.lr-ingest\.io/i,
  // FullStory
  /rs\.fullstory\.com/i,
];

/**
 * Checks if a hostname matches any known telemetry/intake endpoint pattern.
 * These endpoints should never have trace context headers injected.
 */
const isTelemetryEndpoint = (hostname: string): boolean => {
  if (!hostname) return false;
  const lower = hostname.toLowerCase();
  return TELEMETRY_ENDPOINT_PATTERNS.some((pattern) => pattern.test(lower));
};

const normalizeOrigin = (value: string): string | undefined => {
  if (typeof value !== "string" || !value.trim()) return undefined;
  try {
    return new URL(value).origin;
  } catch {
    try {
      if (typeof window !== "undefined") {
        return new URL(value, window.location.origin).origin;
      }
    } catch {
      return undefined;
    }
  }
  return undefined;
};

/**
 * Merges tracestate values per W3C Trace Context spec.
 * The new vendor's entry goes leftmost (owner), other vendors' entries are preserved.
 * Format: "vendor1=value1,vendor2=value2,..."
 *
 * @param newTracestate - The new tracestate from Last9 (goes leftmost)
 * @param existingTracestate - Existing tracestate from other vendors (e.g., DD)
 * @returns Merged tracestate with Last9 leftmost, others preserved
 */
export const mergeTracestates = (newTracestate: string, existingTracestate: string): string => {
  if (!existingTracestate || !existingTracestate.trim()) {
    return newTracestate;
  }
  if (!newTracestate || !newTracestate.trim()) {
    return existingTracestate;
  }

  // Parse new tracestate to get vendor keys we're setting
  const newEntries = new Map<string, string>();
  newTracestate.split(",").forEach((entry) => {
    const trimmed = entry.trim();
    if (!trimmed) return;
    const eqIndex = trimmed.indexOf("=");
    if (eqIndex > 0) {
      const key = trimmed.substring(0, eqIndex).trim();
      newEntries.set(key, trimmed);
    }
  });

  // Parse existing tracestate, keeping entries not in new tracestate
  const preservedEntries: string[] = [];
  existingTracestate.split(",").forEach((entry) => {
    const trimmed = entry.trim();
    if (!trimmed) return;
    const eqIndex = trimmed.indexOf("=");
    if (eqIndex > 0) {
      const key = trimmed.substring(0, eqIndex).trim();
      // Only preserve if not overwritten by new tracestate
      if (!newEntries.has(key)) {
        preservedEntries.push(trimmed);
      }
    }
  });

  // Merge: new entries first (leftmost = owner), then preserved entries
  const mergedEntries = [...newEntries.values(), ...preservedEntries];
  return mergedEntries.join(",");
};

const emitNetworkLog = (
  level: NetworkLogLevel,
  message: string,
  attributes: Record<string, unknown> = {},
  err?: unknown,
) => {
  const payload = {
    message,
    attributes: {
      "l9rum.view.component": "NetworkInstrumentation",
      ...attributes,
    },
    ...(err ? { error: err } : {}),
  };

  if (level === "warn") {
    warn(payload);
    return;
  }

  if (level === "error") {
    error(payload);
    return;
  }

  log(payload);
};

type PatchedFetch = typeof window.fetch & { __l9rum_patched?: boolean };
type PatchedXHR = typeof window.XMLHttpRequest & { __l9rum_patched?: boolean };

type ResolvedIgnorePatterns = {
  fullUrl: (string | RegExp)[];
  pathname: (string | RegExp)[];
  hostname: (string | RegExp)[];
};

// Headers that should be protected from append() when forceOverwrite is enabled
const PROTECTED_TRACE_HEADERS = new Set(["traceparent", "tracestate"]);

// Track if Headers.prototype.append has been patched globally
let headersAppendPatched = false;

// Flag indicating if L9's fetch wrapper is currently processing a request
// Only block trace header append() calls while this is true
// This allows other SDKs to add headers to their OWN telemetry requests
let isL9FetchWrapperActive = false;

// Flag indicating if forceOverwrite mode is enabled
let forceOverwriteEnabled = false;

/**
 * Patches Headers.prototype.append to prevent other SDKs (e.g., DataDog)
 * from appending trace context headers when forceOverwrite is enabled.
 *
 * IMPORTANT: Only blocks append() calls while L9's fetch wrapper is actively
 * processing a request. This allows other SDKs to add headers to their own
 * telemetry requests (which bypass L9's wrapper via shouldIgnoreUrl).
 */
const patchHeadersAppend = () => {
  if (headersAppendPatched) return;
  if (typeof Headers === "undefined") return;

  const originalAppend = Headers.prototype.append;

  Headers.prototype.append = function (name: string, value: string) {
    const lower = name.toLowerCase();

    // Only block trace header append() when:
    // 1. forceOverwrite is enabled in config
    // 2. L9's fetch wrapper is currently active (processing a user request)
    if (forceOverwriteEnabled && isL9FetchWrapperActive && PROTECTED_TRACE_HEADERS.has(lower)) {
      emitNetworkLog("info", `[L9RUM Network] Blocked append() for ${name} (forceOverwrite active)`, {
        "l9rum.view.network.phase": "header_protection",
        "http.request.header": name,
      });
      return;
    }

    return originalAppend.call(this, name, value);
  };

  headersAppendPatched = true;
  emitNetworkLog("info", "[L9RUM Network] Patched Headers.prototype.append for forceOverwrite protection", {
    "l9rum.view.network.phase": "init",
  });
};

/**
 * Enables forceOverwrite mode for trace header protection.
 * Called when forceOverwrite is enabled in configuration.
 */
const enableForceOverwriteMode = () => {
  forceOverwriteEnabled = true;
};

/**
 * Sets the L9 fetch wrapper active state.
 * When active, trace header append() calls from other SDKs are blocked.
 */
const setL9FetchWrapperActive = (active: boolean) => {
  isL9FetchWrapperActive = active;
};

export class NetworkInstrumentation {
  private tracer: Tracer;
  private currentRootSpan: RootSpan = undefined;
  private isPatched = false;
  private observer?: PerformanceObserver;
  private processedKeys: Set<string> = new Set();
  private reportNetworkError?: (error: unknown, context?: Record<string, any>) => void;
  private backendCorrelation: ResolvedBackendCorrelationConfig;
  private enabled: boolean;
  private ignorePatterns: ResolvedIgnorePatterns;
  // Local propagator instances - avoids conflicts with other SDKs (e.g., DataDog)
  private w3cPropagator?: W3CTraceContextPropagator;
  private b3Propagator?: TextMapPropagator;

  constructor(tracer: Tracer, options: NetworkInstrumentationOptions = {}) {
    this.tracer = tracer;
    this.enabled = options.enabled ?? true;
    this.ignorePatterns = this.resolveIgnorePatterns(options.ignorePatterns);
    this.backendCorrelation = this.resolveBackendCorrelationConfig(options.backendCorrelation);
    this.initializePropagators();

    // Patch Headers.prototype.append and enable forceOverwrite mode when configured
    if (this.backendCorrelation.enabled && this.backendCorrelation.forceOverwrite) {
      patchHeadersAppend();
      enableForceOverwriteMode();
    }
  }

  /**
   * Initialize local propagator instances based on configuration.
   * Using local instances instead of global propagation API avoids
   * conflicts when other RUM libraries (e.g., DataDog) register their
   * own propagators globally.
   */
  private initializePropagators() {
    if (!this.backendCorrelation.enabled) return;

    if (this.backendCorrelation.propagationFormats.includes("w3c")) {
      this.w3cPropagator = new W3CTraceContextPropagator();
      emitNetworkLog("info", "[L9RUM Network] W3C Trace Context propagator initialized", {
        "l9rum.view.network.phase": "init",
        "l9rum.view.network.propagator": "w3c",
      });
    }

    if (this.backendCorrelation.propagationFormats.includes("b3")) {
      if (B3Propagator && B3InjectEncoding) {
        // Use B3 multi-header format for broader compatibility
        this.b3Propagator = new B3Propagator({ injectEncoding: B3InjectEncoding.MULTI_HEADER });
        emitNetworkLog("info", "[L9RUM Network] B3 propagator initialized", {
          "l9rum.view.network.phase": "init",
          "l9rum.view.network.propagator": "b3",
        });
      } else {
        emitNetworkLog(
          "warn",
          "[L9RUM Network] B3 propagation format requested but @opentelemetry/propagator-b3 is not installed",
          {
            "l9rum.view.network.phase": "init",
            "l9rum.view.network.propagator": "b3",
          },
        );
      }
    }
  }

  private resolveIgnorePatterns(
    patterns?: NetworkInstrumentationOptions["ignorePatterns"],
  ): ResolvedIgnorePatterns {
    const fullUrl = Array.isArray(patterns?.fullUrl)
      ? patterns.fullUrl.filter(
          (p): p is string | RegExp => typeof p === "string" || p instanceof RegExp,
        )
      : [];
    const pathname = Array.isArray(patterns?.pathname)
      ? patterns.pathname.filter(
          (p): p is string | RegExp => typeof p === "string" || p instanceof RegExp,
        )
      : [];
    const hostname = Array.isArray(patterns?.hostname)
      ? patterns.hostname.filter(
          (p): p is string | RegExp => typeof p === "string" || p instanceof RegExp,
        )
      : [];

    return {
      fullUrl,
      pathname,
      hostname,
    };
  }

  private resolveBackendCorrelationConfig(
    config?: BackendCorrelationConfig,
  ): ResolvedBackendCorrelationConfig {
    const enabled = config?.enabled ?? DEFAULT_BACKEND_CORRELATION_CONFIG.enabled;
    const injectToAllRequests =
      config?.injectToAllRequests ?? DEFAULT_BACKEND_CORRELATION_CONFIG.injectToAllRequests;
    const propagationFormats =
      Array.isArray(config?.propagationFormats) && config?.propagationFormats.length
        ? Array.from(
            new Set(
              config!.propagationFormats
                .map((format) => (format === "b3" ? "b3" : "w3c"))
                .filter((format): format is "w3c" | "b3" => format === "w3c" || format === "b3"),
            ),
          )
        : [...DEFAULT_BACKEND_CORRELATION_CONFIG.propagationFormats];
    const corsAllowedOrigins =
      Array.isArray(config?.corsAllowedOrigins) && config.corsAllowedOrigins.length
        ? config.corsAllowedOrigins.filter((origin): origin is string => typeof origin === "string")
        : [...DEFAULT_BACKEND_CORRELATION_CONFIG.corsAllowedOrigins];
    const normalizedAllowedOrigins = corsAllowedOrigins
      .map((origin) => normalizeOrigin(origin))
      .filter((origin): origin is string => !!origin);
    const customHeaders =
      config?.customHeaders && typeof config.customHeaders === "object"
        ? Object.entries(config.customHeaders).reduce(
            (acc, [key, value]) => {
              if (typeof key === "string" && key.trim().length && typeof value === "string") {
                acc[key] = value;
              }
              return acc;
            },
            {} as Record<string, string>,
          )
        : { ...DEFAULT_BACKEND_CORRELATION_CONFIG.customHeaders };

    const forceOverwrite =
      config?.forceOverwrite ?? DEFAULT_BACKEND_CORRELATION_CONFIG.forceOverwrite;

    return {
      enabled,
      propagationFormats: propagationFormats.length
        ? propagationFormats
        : [...DEFAULT_BACKEND_CORRELATION_CONFIG.propagationFormats],
      corsAllowedOrigins,
      customHeaders,
      injectToAllRequests,
      normalizedAllowedOrigins,
      forceOverwrite,
    };
  }

  private shouldInjectBackendCorrelation(url: string): boolean {
    if (!this.backendCorrelation.enabled) return false;
    if (typeof window === "undefined") {
      return this.backendCorrelation.injectToAllRequests;
    }

    try {
      const parsed = new URL(url, window.location.origin);

      // NEVER inject trace headers into telemetry/intake endpoints
      // These are internal SDK endpoints that should not have cross-SDK trace context
      if (isTelemetryEndpoint(parsed.hostname)) {
        emitNetworkLog("info", "[L9RUM Network] Skipping header injection for telemetry endpoint", {
          "l9rum.view.network.phase": "backend_correlation",
          "http.url": url,
          "net.peer.name": parsed.hostname,
        });
        return false;
      }

      if (this.backendCorrelation.injectToAllRequests) return true;

      if (parsed.origin === window.location.origin) {
        return true;
      }
      return this.backendCorrelation.normalizedAllowedOrigins.includes(parsed.origin);
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Failed to evaluate backend correlation injection",
        {
          "l9rum.view.network.guard": "backend_correlation_url",
          "http.url": url,
        },
        e,
      );
      return false;
    }
  }

  /**
   * Injects trace context headers from a propagator into the carrier.
   * Uses local propagator instance to avoid conflicts with other SDKs
   * (e.g., DataDog RUM) that may register their own global propagators.
   */
  private injectWithPropagator(
    propagator: TextMapPropagator,
    injectionContext: Context,
    headersToSet: Record<string, string>,
    existingHeaders: Set<string>,
    existingHeaderValues: Record<string, string>,
  ) {
    const carrier: Record<string, string> = {};
    const setter: TextMapSetter<Record<string, string>> = {
      set: (c, key, value) => {
        c[key] = value;
      },
    };

    propagator.inject(injectionContext, carrier, setter);

    Object.entries(carrier).forEach(([key, value]) => {
      const lower = key.toLowerCase();

      if (this.backendCorrelation.forceOverwrite) {
        // W3C Trace Context compliant: overwrite traceparent, but merge tracestate
        // to preserve other vendors' entries (e.g., Datadog's dd=s:1;o:rum)
        if (lower === "tracestate" && existingHeaderValues[lower]) {
          // Merge: Last9's tracestate entries first (leftmost = owner),
          // then preserve other vendors' entries
          headersToSet[key] = mergeTracestates(value, existingHeaderValues[lower]);
        } else {
          headersToSet[key] = value;
        }
        existingHeaders.add(lower);
      } else if (!existingHeaders.has(lower)) {
        headersToSet[key] = value;
        existingHeaders.add(lower);
      }
    });
  }

  private buildBackendCorrelationHeaderMap(
    url: string,
    injectionContext: Context,
    existingHeaders: Set<string>,
    existingHeaderValues: Record<string, string> = {},
  ): Record<string, string> {
    if (!this.backendCorrelation.enabled) return {};
    if (!this.shouldInjectBackendCorrelation(url)) return {};

    const headersToSet: Record<string, string> = {};

    // Use local W3C propagator instance (avoids DD RUM global propagator conflict)
    if (this.w3cPropagator) {
      this.injectWithPropagator(
        this.w3cPropagator,
        injectionContext,
        headersToSet,
        existingHeaders,
        existingHeaderValues,
      );
    }

    // Use local B3 propagator instance
    if (this.b3Propagator) {
      this.injectWithPropagator(
        this.b3Propagator,
        injectionContext,
        headersToSet,
        existingHeaders,
        existingHeaderValues,
      );
    }

    Object.entries(this.backendCorrelation.customHeaders).forEach(([key, value]) => {
      if (!key || typeof value !== "string") return;
      const lower = key.toLowerCase();
      if (existingHeaders.has(lower)) return;
      headersToSet[key] = value;
      existingHeaders.add(lower);
    });

    return headersToSet;
  }

  private applyBackendCorrelationToFetchRequest(
    request: Request,
    url: string,
    span: Span,
    parentContext: Context | undefined,
  ): Request {
    if (!this.backendCorrelation.enabled) {
      return request;
    }

    const injectionContext = trace.setSpan(parentContext ?? context.active(), span);

    try {
      const headers = new Headers(request.headers || undefined);
      const existing = new Set<string>();
      const existingValues: Record<string, string> = {};
      headers.forEach((value, key) => {
        const lower = key.toLowerCase();
        existing.add(lower);
        existingValues[lower] = value;
      });
      const headersToSet = this.buildBackendCorrelationHeaderMap(
        url,
        injectionContext,
        existing,
        existingValues,
      );
      const headerEntries = Object.entries(headersToSet);
      if (!headerEntries.length) {
        return request;
      }
      headerEntries.forEach(([key, value]) => headers.set(key, value));

      return new Request(request, { headers });
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Failed to inject backend correlation headers for fetch",
        {
          "l9rum.view.network.guard": "backend_correlation_fetch",
          "http.url": url,
        },
        e,
      );
      return request;
    }
  }

  private applyBackendCorrelationToXHR(
    xhr: XMLHttpRequest,
    url: string,
    span: Span,
    parentContext: Context | undefined,
  ) {
    if (!this.backendCorrelation.enabled) return;

    const injectionContext = trace.setSpan(parentContext ?? context.active(), span);
    const recorded = (xhr as any).__l9rum_headers || {};
    const existing = new Set<string>();
    const existingValues: Record<string, string> = {};
    try {
      Object.entries(recorded).forEach(([key, value]) => {
        const lower = key.toLowerCase();
        existing.add(lower);
        if (typeof value === "string") {
          existingValues[lower] = value;
        }
      });
    } catch {}

    try {
      const headersToSet = this.buildBackendCorrelationHeaderMap(
        url,
        injectionContext,
        existing,
        existingValues,
      );
      Object.entries(headersToSet).forEach(([key, value]) => {
        try {
          xhr.setRequestHeader(key, value);
        } catch (err) {
          emitNetworkLog(
            "warn",
            "[L9RUM Network] Failed setting backend correlation header for XHR",
            {
              "l9rum.view.network.guard": "backend_correlation_xhr",
              "http.url": url,
              "http.request.header": key,
            },
            err,
          );
        }
      });
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Failed to inject backend correlation headers for XHR",
        {
          "l9rum.view.network.guard": "backend_correlation_xhr",
          "http.url": url,
        },
        e,
      );
    }
  }

  public subscribe(
    rootSpan: RootSpan,
    reportNetworkError?: (error: unknown, context?: Record<string, any>) => void,
  ) {
    this.currentRootSpan = rootSpan;
    this.reportNetworkError = reportNetworkError;
    if (!this.enabled) {
      emitNetworkLog(
        "info",
        "[L9RUM Network] Network tracking is disabled, skipping instrumentation",
        {
          "l9rum.view.network.phase": "subscribe",
        },
      );
      return;
    }
    if (!this.isPatched) {
      this.patchFetch();
      this.patchXHR();
      this.isPatched = true;
      emitNetworkLog(
        "info",
        "[L9RUM Network] Patched fetch and XMLHttpRequest for instrumentation",
        {
          "l9rum.view.network.phase": "subscribe",
        },
      );
    }
    this.observeResources();
  }

  public unsubscribe() {
    this.currentRootSpan = undefined;
    this.reportNetworkError = undefined;
    try {
      this.observer?.disconnect?.();
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Failed to disconnect PerformanceObserver",
        {
          "l9rum.view.network.phase": "unsubscribe",
        },
        e,
      );
    }
  }

  private getParentContext() {
    return this.currentRootSpan ? trace.setSpan(context.active(), this.currentRootSpan) : undefined;
  }

  private isLocalhost(hostname: string): boolean {
    if (!hostname) return false;
    const normalized = hostname.toLowerCase().trim();
    return (
      normalized === "localhost" ||
      normalized === "127.0.0.1" ||
      normalized === "::1" ||
      normalized === "[::1]" ||
      normalized === "0:0:0:0:0:0:0:1" ||
      normalized === "[0:0:0:0:0:0:0:1]" ||
      normalized.startsWith("127.")
    );
  }

  private matchesPattern(value: string, pattern: string | RegExp): boolean {
    if (typeof pattern === "string") {
      return value.includes(pattern);
    }
    if (pattern instanceof RegExp) {
      try {
        return pattern.test(value);
      } catch {
        return false;
      }
    }
    return false;
  }

  private shouldIgnoreUrl(rawUrl: string): boolean {
    if (!this.enabled) {
      return true;
    }

    try {
      const u = new URL(rawUrl, window.location.origin);
      const hostname = u.hostname || "";
      const pathname = u.pathname || "";
      const fullUrl = rawUrl;

      // Default localhost ignore
      if (this.isLocalhost(hostname)) {
        return true;
      }

      // Check ignore patterns
      // Test fullUrl patterns against full URL
      for (const pattern of this.ignorePatterns.fullUrl) {
        if (this.matchesPattern(fullUrl, pattern)) {
          return true;
        }
      }

      // Test pathname patterns against pathname
      for (const pattern of this.ignorePatterns.pathname) {
        if (this.matchesPattern(pathname, pattern)) {
          return true;
        }
      }

      // Test hostname patterns against hostname
      for (const pattern of this.ignorePatterns.hostname) {
        if (this.matchesPattern(hostname, pattern)) {
          return true;
        }
      }

      const host = (u as any).hostname || (u as any).host || "";
      const path = u.pathname || "";
      if (host && host.includes("otlp-ext-") && path.includes("/telemetry/beacon/")) {
        return true;
      }

      // Also ignore all known telemetry/intake endpoints from other SDKs
      // This prevents creating orphaned spans for RUM telemetry requests
      if (isTelemetryEndpoint(hostname)) {
        return true;
      }
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Error parsing URL in shouldIgnoreUrl",
        {
          "l9rum.view.network.guard": "parse_url",
        },
        e,
      );
    }
    return false;
  }

  private addCommonHttpAttributes(span: Span, url: string, method: string, initiator: string) {
    const attrs: Attributes = {};
    attrs[CONV.HTTP_URL] = url;
    attrs[CONV.HTTP_METHOD] = method;
    attrs[CONV.INITIATOR_TYPE] = initiator;

    try {
      const u = new URL(url, window.location.origin);
      attrs[CONV.NET_PEER_NAME] = u.host;
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Error parsing URL in addCommonHttpAttributes",
        {
          "l9rum.view.network.guard": "parse_url",
        },
        e,
      );
    }

    span.setAttributes(attrs);
  }

  private tryAttachPerformanceTimings(
    span: Span,
    url: string,
  ): PerformanceResourceTiming | undefined {
    try {
      const entry = this.getLatestPerformanceEntry(url);
      if (entry) {
        addSpanNetworkEvents(span, entry);
        return entry;
      }
      return undefined;
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Unable to attach performance timings",
        {
          "l9rum.view.network.guard": "performance_timings",
        },
        e,
      );
      return undefined;
    }
  }

  private getLatestPerformanceEntry(url: string): PerformanceResourceTiming | undefined {
    try {
      const perf = otperformance as unknown as Performance;
      const entries = (perf.getEntriesByName?.(url, "resource") ||
        []) as PerformanceResourceTiming[];
      if (entries && entries.length > 0) {
        return entries[entries.length - 1];
      }
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Failed to read performance entries",
        {
          "l9rum.view.network.guard": "performance_entries",
        },
        e,
      );
    }
    return undefined;
  }

  private notifyNetworkError(error: unknown, context: Record<string, any> = {}) {
    if (!this.reportNetworkError) return;
    try {
      this.reportNetworkError(error, context);
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Failed to forward network error",
        {
          "l9rum.view.network.guard": "notify_network_error",
        },
        e,
      );
    }
  }

  private buildNetworkErrorContext(details: {
    url: string;
    method: string;
    initiator: string;
    phase: string;
    status?: number;
    contentLength?: number;
    timingEntry?: PerformanceResourceTiming;
    error?: unknown;
  }): Record<string, any> {
    const context: Record<string, any> = {};
    const method = (details.method || "GET").toUpperCase();
    context[CONV.HTTP_URL] = details.url;
    context[CONV.HTTP_METHOD] = method;
    context[CONV.INITIATOR_TYPE] = details.initiator;
    if (typeof details.status === "number") context[CONV.HTTP_STATUS_CODE] = details.status;
    if (
      typeof details.contentLength === "number" &&
      !Number.isNaN(details.contentLength) &&
      details.contentLength >= 0
    ) {
      context[CONV.HTTP_RESPONSE_CONTENT_LENGTH] = details.contentLength;
    }
    const path = this.extractPath(details.url);
    context["network.phase"] = details.phase;
    context["network.request_path"] = path;
    const errorMessage =
      details.error instanceof Error
        ? details.error.message
        : typeof details.error === "string"
          ? details.error
          : details.error && typeof details.error === "object"
            ? (details.error as any)?.message
            : undefined;
    if (errorMessage) {
      context["network.error_message"] = errorMessage;
    }
    if (details.timingEntry) {
      const entry = details.timingEntry;
      if (typeof entry.transferSize === "number" && entry.transferSize >= 0) {
        context[CONV.NETWORK_TRANSFER_SIZE] = entry.transferSize;
      }
      if (typeof entry.encodedBodySize === "number" && entry.encodedBodySize >= 0) {
        context[CONV.SIZE] = entry.encodedBodySize;
      }
      try {
        context[CONV.NETWORK_TIMINGS] = JSON.stringify({
          duration: entry.duration,
          fetchStart: entry.fetchStart,
          requestStart: entry.requestStart,
          responseStart: entry.responseStart,
          responseEnd: entry.responseEnd,
        });
      } catch (e) {
        emitNetworkLog(
          "warn",
          "[L9RUM Network] Failed to serialise network timings",
          {
            "l9rum.view.network.guard": "network_timings_serialize",
          },
          e,
        );
      }
    }
    context.message = this.formatNetworkMessage(method, details.url, details.status, details.phase);
    return context;
  }

  private formatNetworkMessage(method: string, url: string, status?: number, phase?: string) {
    const path = this.extractPath(url);
    if (typeof status === "number" && status >= 400) {
      return `${method} ${path} failed with HTTP ${status}`;
    }
    if (phase === "abort") {
      return `${method} ${path} request aborted`;
    }
    if (phase === "request") {
      return `${method} ${path} request failed`;
    }
    if (phase === "wrapper") {
      return `${method} ${path} instrumentation error`;
    }
    return `${method} ${path} network error`;
  }

  private extractPath(rawUrl: string) {
    try {
      const parsed = new URL(rawUrl, window.location.origin);
      return parsed.pathname || rawUrl;
    } catch {
      return rawUrl;
    }
  }

  // fetch
  private patchFetch() {
    const w = window as any;
    const originalFetch: PatchedFetch = w.fetch;
    if (!originalFetch || originalFetch.__l9rum_patched) return;

    const tracer = this.tracer;
    const getParentContext = this.getParentContext.bind(this);
    const addCommonHttpAttributes = this.addCommonHttpAttributes.bind(this);
    const tryAttachPerformanceTimings = this.tryAttachPerformanceTimings.bind(this);
    const shouldIgnoreUrl = this.shouldIgnoreUrl.bind(this);
    const notifyNetworkError = this.notifyNetworkError.bind(this);
    const buildNetworkErrorContext = this.buildNetworkErrorContext.bind(this);
    const applyBackendCorrelationToFetchRequest =
      this.applyBackendCorrelationToFetchRequest.bind(this);

    const patched: PatchedFetch = async function (input: RequestInfo | URL, init?: RequestInit) {
      // Track if we've already notified about this failure inside the inner block
      let innerNotified = false;
      try {
        let request = input instanceof Request ? input : new Request(input, init);
        const url = request.url;

        const method = (request.method || "GET").toUpperCase();
        const u = new URL(url, window.location.origin);
        const spanName = `${method} ${u.pathname}`;

        if (shouldIgnoreUrl(url)) {
          return originalFetch.call(w, request);
        }

        const parentContext = getParentContext();
        if (!parentContext) {
          // No current view root span; avoid creating orphaned root spans
          return originalFetch.call(w, request);
        }
        const span = tracer.startSpan(
          spanName,
          {
            kind: SpanKind.CLIENT,
          },
          parentContext,
        );
        addCommonHttpAttributes(span, url, method, "fetch");

        request = applyBackendCorrelationToFetchRequest(request, url, span, parentContext);

        let response: Response;
        try {
          // Set active flag to block other SDKs from appending trace headers
          // during the original fetch call (which triggers their wrappers)
          setL9FetchWrapperActive(true);
          try {
            response = await originalFetch.call(w, request);
          } finally {
            setL9FetchWrapperActive(false);
          }
        } catch (err: any) {
          emitNetworkLog(
            "warn",
            "[L9RUM Network] fetch call failed",
            {
              "l9rum.view.network.guard": "fetch_call",
              "http.method": method,
              "http.url": url,
            },
            err,
          );
          notifyNetworkError(
            err,
            buildNetworkErrorContext({
              url,
              method,
              initiator: "fetch",
              phase: "request",
              error: err,
            }),
          );
          // Mark that we've already sent a network error notification for this failure
          innerNotified = true;
          if (typeof span.recordException === "function") span.recordException(err);
          if (typeof span.setStatus === "function") {
            span.setStatus({ code: SpanStatusCode.ERROR, message: String(err?.message || err) });
          }
          span.end();
          throw err;
        }

        try {
          const status = response.status;
          span.setAttribute(CONV.HTTP_STATUS_CODE, status);
          const cl = response.headers?.get?.("content-length");
          let parsedContentLength: number | undefined;
          if (cl) {
            const num = Number(cl);
            if (!Number.isNaN(num)) {
              parsedContentLength = num;
              span.setAttribute(CONV.HTTP_RESPONSE_CONTENT_LENGTH, num);
            }
          }
          const timingEntry = tryAttachPerformanceTimings(span, url);
          if (typeof span.setStatus === "function") {
            if (status >= 400)
              span.setStatus({ code: SpanStatusCode.ERROR, message: `HTTP ${status}` });
            else span.setStatus({ code: SpanStatusCode.OK });
          }
          if (status >= 400) {
            const httpError = new Error(`HTTP ${status}`);
            httpError.name = "HttpError";
            notifyNetworkError(
              httpError,
              buildNetworkErrorContext({
                url,
                method,
                initiator: "fetch",
                status,
                phase: "response",
                contentLength: parsedContentLength,
                timingEntry,
              }),
            );
          }
        } catch (e) {
          emitNetworkLog(
            "warn",
            "[L9RUM Network] Failed to finalize fetch span metadata",
            {
              "l9rum.view.network.guard": "fetch_finalize",
            },
            e,
          );
        } finally {
          span.end();
        }

        return response;
      } catch (outer) {
        emitNetworkLog(
          "warn",
          "[L9RUM Network] fetch wrapper threw",
          {
            "l9rum.view.network.guard": "fetch_wrapper",
          },
          outer,
        );
        // Avoid duplicate reporting if inner block already notified the network error
        if (!innerNotified) {
          notifyNetworkError(
            outer,
            buildNetworkErrorContext({
              url: String((input as any)?.url || input),
              method:
                typeof (init as any)?.method === "string"
                  ? (init as any).method
                  : input instanceof Request
                    ? input.method
                    : "GET",
              initiator: "fetch",
              phase: "wrapper",
              error: outer,
            }),
          );
        }
        throw outer;
      }
    } as any;

    patched.__l9rum_patched = true;
    w.fetch = patched;
  }

  // XHR
  private patchXHR() {
    const XHR: PatchedXHR = window.XMLHttpRequest as any;
    if (!XHR || (XHR as any).__l9rum_patched) return;

    const tracer = this.tracer;
    const getParentContext = this.getParentContext.bind(this);
    const addCommonHttpAttributes = this.addCommonHttpAttributes.bind(this);
    const tryAttachPerformanceTimings = this.tryAttachPerformanceTimings.bind(this);
    const shouldIgnoreUrl = this.shouldIgnoreUrl.bind(this);
    const notifyNetworkError = this.notifyNetworkError.bind(this);
    const buildNetworkErrorContext = this.buildNetworkErrorContext.bind(this);
    const applyBackendCorrelationToXHR = this.applyBackendCorrelationToXHR.bind(this);

    const OriginalOpen = XHR.prototype.open;
    const OriginalSend = XHR.prototype.send;
    const OriginalSetRequestHeader = XHR.prototype.setRequestHeader;

    XHR.prototype.open = function (
      this: XMLHttpRequest,
      method: string,
      url: string,
      ...rest: any[]
    ) {
      (this as any).__l9rum_method = (method || "GET").toUpperCase();
      (this as any).__l9rum_url = url;
      return (OriginalOpen as any).apply(this, [method, url, ...rest]);
    } as any;

    XHR.prototype.setRequestHeader = function (
      this: XMLHttpRequest,
      header: string,
      value: string,
    ) {
      try {
        (this as any).__l9rum_headers = (this as any).__l9rum_headers || {};
        (this as any).__l9rum_headers[header] = value;
      } catch (e) {
        emitNetworkLog(
          "warn",
          "[L9RUM Network] Failed recording request header",
          {
            "l9rum.view.network.guard": "xhr_request_header",
            "http.request.header": header,
          },
          e,
        );
      }
      return OriginalSetRequestHeader.apply(this, [header, value]);
    } as any;

    XHR.prototype.send = function (this: XMLHttpRequest, body?: Document | BodyInit | null) {
      const url: string = (this as any).__l9rum_url || "";
      const method: string = (this as any).__l9rum_method || "GET";
      const u = new URL(url, window.location.origin);
      const spanName = `${method} ${u.pathname}`;

      if (shouldIgnoreUrl(url)) {
        return (OriginalSend as any).apply(this, [body as any]);
      }

      const parentContext = getParentContext();
      if (!parentContext) {
        // No current view root span; avoid creating orphaned root spans
        return (OriginalSend as any).apply(this, [body as any]);
      }
      const span = tracer.startSpan(
        spanName,
        {
          kind: SpanKind.CLIENT,
        },
        parentContext,
      );
      addCommonHttpAttributes(span, url, method, "xmlhttprequest");
      applyBackendCorrelationToXHR(this, url, span, parentContext);

      const onLoadEnd = () => {
        try {
          const status = (this.status as any) ?? 0;
          span.setAttribute(CONV.HTTP_STATUS_CODE, status);
          let parsedContentLength: number | undefined;
          try {
            const cl = this.getResponseHeader("content-length");
            if (cl) {
              const num = Number(cl);
              if (!Number.isNaN(num)) {
                parsedContentLength = num;
                span.setAttribute(CONV.HTTP_RESPONSE_CONTENT_LENGTH, num);
              }
            }
          } catch (e) {
            emitNetworkLog(
              "warn",
              "[L9RUM Network] Failed reading content-length header",
              {
                "l9rum.view.network.guard": "xhr_content_length",
              },
              e,
            );
          }
          const timingEntry = tryAttachPerformanceTimings(span, url);
          if (typeof span.setStatus === "function") {
            if (status >= 400)
              span.setStatus({ code: SpanStatusCode.ERROR, message: `HTTP ${status}` });
            else span.setStatus({ code: SpanStatusCode.OK });
          }
          if (status >= 400) {
            const httpError = new Error(`HTTP ${status}`);
            httpError.name = "HttpError";
            notifyNetworkError(
              httpError,
              buildNetworkErrorContext({
                url,
                method,
                initiator: "xmlhttprequest",
                status,
                phase: "response",
                contentLength: parsedContentLength,
                timingEntry,
              }),
            );
          }
        } catch (e) {
          emitNetworkLog(
            "warn",
            "[L9RUM Network] Failed to finalize XHR span metadata",
            {
              "l9rum.view.network.guard": "xhr_finalize",
            },
            e,
          );
        } finally {
          span.end();
          this.removeEventListener("loadend", onLoadEnd);
          this.removeEventListener("error", onError);
          this.removeEventListener("abort", onAbort);
        }
      };

      const onError = (ev: any) => {
        try {
          notifyNetworkError(
            ev?.error || ev,
            buildNetworkErrorContext({
              url,
              method,
              initiator: "xmlhttprequest",
              phase: "response",
              status: this.status as any,
              error: ev?.error || ev,
            }),
          );
          if (typeof span.recordException === "function") span.recordException(ev?.error || ev);
          if (typeof span.setStatus === "function") {
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: String(ev?.message || "XHR error"),
            });
          }
        } finally {
          span.end();
        }
      };

      const onAbort = () => {
        try {
          notifyNetworkError(
            new Error("Request aborted"),
            buildNetworkErrorContext({
              url,
              method,
              initiator: "xmlhttprequest",
              phase: "abort",
            }),
          );
          if (typeof span.setStatus === "function")
            span.setStatus({ code: SpanStatusCode.ERROR, message: "aborted" });
        } finally {
          span.end();
        }
      };

      this.addEventListener("loadend", onLoadEnd);
      this.addEventListener("error", onError);
      this.addEventListener("abort", onAbort);

      try {
        return (OriginalSend as any).apply(this, [body as any]);
      } catch (err) {
        emitNetworkLog(
          "warn",
          "[L9RUM Network] XHR send threw",
          {
            "l9rum.view.network.guard": "xhr_send",
            "http.method": method,
            "http.url": url,
          },
          err,
        );
        notifyNetworkError(
          err,
          buildNetworkErrorContext({
            url,
            method,
            initiator: "xmlhttprequest",
            phase: "request",
            error: err,
          }),
        );
        try {
          if (typeof span.recordException === "function") span.recordException(err as any);
          if (typeof span.setStatus === "function") {
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: String((err as any)?.message || err),
            });
          }
        } finally {
          span.end();
        }
        throw err;
      }
    } as any;

    (XHR as any).__l9rum_patched = true;
    emitNetworkLog("info", "[L9RUM Network] XMLHttpRequest patched", {
      "l9rum.view.network.phase": "subscribe",
      "l9rum.view.network.guard": "xhr_patch",
    });
  }

  // ResourceObserver (non-fetch/xhr live; include buffered all)
  private observeResources() {
    // buffered scan
    this.processBufferedResources();
    try {
      this.observer = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceResourceTiming[];
        entries.forEach((entry) => this.maybeCreateResourceSpan(entry, false));
      });
      this.observer.observe({ type: "resource", buffered: false } as any);
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Failed to start resource PerformanceObserver",
        {
          "l9rum.view.network.guard": "resource_observer",
        },
        e,
      );
    }
  }

  private processBufferedResources() {
    try {
      const perf = otperformance as unknown as Performance;
      const list = (perf.getEntriesByType?.("resource") || []) as PerformanceResourceTiming[];
      list.forEach((e) => this.maybeCreateResourceSpan(e, true));
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Failed to process buffered resource entries",
        {
          "l9rum.view.network.guard": "buffered_resources",
        },
        e,
      );
    }
  }

  private buildKey(entry: PerformanceResourceTiming): string {
    return `${entry.name}|${Math.floor(entry.startTime)}|${Math.floor(entry.duration)}`;
  }

  private shouldSkipForLive(entry: PerformanceResourceTiming): boolean {
    const t = (entry.initiatorType || "").toLowerCase();
    return t === "fetch" || t === "xmlhttprequest";
  }

  private maybeCreateResourceSpan(entry: PerformanceResourceTiming, isBuffered: boolean) {
    if (!this.currentRootSpan) return;
    if (!isBuffered && this.shouldSkipForLive(entry)) return;
    if (this.shouldIgnoreUrl(entry.name)) return;

    const key = this.buildKey(entry);
    if (this.processedKeys.has(key)) return;
    this.processedKeys.add(key);

    const parentContext = trace.setSpan(context.active(), this.currentRootSpan);
    const u = new URL(entry.name, window.location.origin);
    const spanName = u.pathname;
    const span: Span = this.tracer.startSpan(
      spanName,
      {
        startTime: entry.startTime,
      },
      parentContext,
    );

    try {
      span.setStatus({ code: SpanStatusCode.OK });
      span.setAttribute(CONV.HTTP_URL, entry.name);
      span.setAttribute(CONV.NET_PEER_NAME, u.host);
      span.setAttribute(CONV.INITIATOR_TYPE, entry.initiatorType);
      addSpanNetworkEvents(span, entry);
    } catch (e) {
      emitNetworkLog(
        "warn",
        "[L9RUM Network] Failed to populate resource span",
        {
          "l9rum.view.network.guard": "resource_span",
          "http.url": entry.name,
        },
        e,
      );
    } finally {
      try {
        span.end(entry.responseEnd || undefined);
      } catch (e) {
        emitNetworkLog(
          "warn",
          "[L9RUM Network] Failed to end span with responseEnd timestamp",
          {
            "l9rum.view.network.guard": "resource_span_end",
          },
          e,
        );
        span.end();
      }
    }
  }
}
